import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';

export const useSettings = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { t, currentLanguage, changeLanguage } = useI18n();
  const { profile, isLoading: isProfileLoading } = useUserProfile();

  const [isSignOutConfirmOpen, setIsSignOutConfirmOpen] = useState(false);
  const [isPreferencesExpanded, setIsPreferencesExpanded] = useState(false);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const togglePreferences = useCallback(() => {
    setIsPreferencesExpanded(prev => !prev);
  }, []);

  const handleSignOutRequest = useCallback(() => {
    setIsSignOutConfirmOpen(true);
  }, []);

  const handleSignOutConfirm = useCallback(async () => {
    try {
      setIsSignOutConfirmOpen(false);
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut, navigate]);

  const handleSignOutCancel = useCallback(() => {
    setIsSignOutConfirmOpen(false);
  }, []);

  return {
    // Data
    profile,
    isProfileLoading,
    userEmail: user?.email ?? '',
    isInstructor: profile?.is_instructor ?? false,
    currentLanguage,
    changeLanguage,

    // UI state
    isSignOutConfirmOpen,
    isPreferencesExpanded,

    // Handlers
    handleNavigate,
    togglePreferences,
    handleSignOutRequest,
    handleSignOutConfirm,
    handleSignOutCancel,

    // i18n
    t,
  };
};
